import React, { useState, useMemo, useEffect } from 'react';
import {
  View,
  Text,
  StyleSheet,
  TouchableOpacity,
  Platform,
  Dimensions,
} from 'react-native';

import { SummaryAttachmentRow } from './SummaryAttachmentRow';
import { SummaryRichTextValue } from './SummaryRichTextValue';
import {
  ChevronDown,
  ChevronUp,
  Expand,
  Minimize,
} from '../../../components/icons';
import { ImpersonationActivitySection } from '../../../components/impersonation/ImpersonationActivitySection';
import { AttachmentCarouselModal } from '../../../components/modals/AttachmentCarouselModal';
import { type RequestFormStepId } from '../../../config/requestFormStepRegistry';
import {
  getSectionLabel,
  stripStageNumber,
} from '../../../constants/sectionIds';
import { useAuth } from '../../../contexts/AuthContext';
import { useRequestScope } from '../../../contexts/RequestScopeContext';
import { useSidebarStepMetrics } from '../../../contexts/SidebarStepMetricsContext';
import {
  debugAttachmentGrouping,
  registerAttachmentGroupingDebugGlobals,
  summarizeGroupedAttachments,
  summarizeSectionAttachmentCounts,
} from '../../../dev/attachmentGroupingDebug';
import {
  debugAttachmentScope,
  registerAttachmentScopeDebugGlobals,
} from '../../../dev/attachmentScopeDebug';
import { useCoolingSystemConfigurations } from '../../../hooks/queries/useCoolingSystemConfigurations';
import { useLargeUpsConfigurations } from '../../../hooks/queries/useLargeUpsConfigurations';
import { useLiquidCoolingCduConfigurations } from '../../../hooks/queries/useLiquidCoolingCduConfigurations';
import { usePowerDistributionConfigurations } from '../../../hooks/queries/usePowerDistributionConfigurations';
import { useRackConfigurations } from '../../../hooks/queries/useRackConfigurations';
import { useSmallUpsConfigurations } from '../../../hooks/queries/useSmallUpsConfigurations';
import { useAttachmentCarousel } from '../../../hooks/useAttachmentCarousel';
import { useFieldPermissions } from '../../../hooks/useFieldPermissions';
import { useLocationTagNameLookup } from '../../../hooks/useLocationTagNameLookup';
import {
  COOLING_EDITOR_SHARED_FIELD_KEYS,
  COOLING_SYSTEM_SUMMARY_METRIC_FIELDS,
  LARGE_UPS_SUMMARY_METRIC_FIELDS,
  LIQUID_COOLING_CDU_EDITOR_SHARED_FIELD_KEYS,
  LIQUID_COOLING_CDU_SUMMARY_METRIC_FIELDS,
  POWER_DISTRIBUTION_SUMMARY_METRIC_FIELDS,
  RACK_FORM_SUMMARY_FIELD_ORDER,
  SMALL_UPS_SUMMARY_METRIC_FIELDS,
} from '../../../request/fields/requestFieldRegistry';
import {
  SavedAttachment,
  filterInlineAttachmentsFromSectionMap,
} from '../../../services/attachmentService';
import {
  getCoolingSharedSummaryDefinitions,
  getPerCoolingSystemSummaryDefinitions,
  coolingSystemSummaryUnitRowValue,
  resolveSummaryCoolingSystemConfigurations,
} from '../../../summary/coolingSystemSummaryDisplay';
import {
  getPerLargeUpsSummaryDefinitions,
  largeUpsSummaryUnitRowValue,
  resolveSummaryLargeUpsConfigurations,
} from '../../../summary/largeUpsSummaryDisplay';
import {
  getPerLiquidCoolingCduSummaryDefinitions,
  getLiquidCoolingSharedSummaryDefinitions,
  liquidCoolingCduSummaryUnitRowValue,
  resolveSummaryLiquidCoolingCduConfigurations,
} from '../../../summary/liquidCoolingCduSummaryDisplay';
import {
  getPerPowerDistributionSummaryDefinitions,
  powerDistributionSummaryUnitRowValue,
  resolveSummaryPowerDistributionConfigurations,
} from '../../../summary/powerDistributionSummaryDisplay';
import {
  getPerRackSummaryDefinitions,
  rackSummaryUnitRowValue,
  resolveSummaryRackConfigurations,
} from '../../../summary/racksSummaryDisplay';
import { withRepeatableSummaryPlaceholderUnit } from '../../../summary/repeatableSummaryDisplayUnits';
import {
  getPerSmallUpsSummaryDefinitions,
  resolveSummarySmallUpsConfigurations,
  smallUpsSummaryUnitRowValue,
} from '../../../summary/smallUpsSummaryDisplay';
import { SUMMARY_CARD_SOURCES } from '../../../summary/summaryCardRegistry';
import {
  resolveSummarySectionMetrics,
  filterSummaryRowsForFormView,
} from '../../../summary/summaryFieldMetrics';
import { isSummaryRowGateSatisfied } from '../../../summary/summaryRowVisibility';
import { getSummarySubsectionDisplayTitle } from '../../../summary/summarySubsectionTitles';
import {
  buildGroupedAttachmentsFromSectionMap,
  formatGroupedSubstageLabel,
  resolveAttachmentScopeOptions,
  resolveStoredStepIdsForAttachments,
} from '../../../utils/attachmentHelpers';
import { resolvePowerAdditionalInformationSummaryLabel } from '../../../utils/facilityPowerAdditionalInformation';
import { POWER_DISTRIBUTION_STAGE_GATE_MESSAGE } from '../../../utils/facilityPowerScopeSections';
import {
  FACILITY_POWER_SUMMARY_ACCORDION_TITLES,
  resolveFacilityPowerSummaryAccordionMetrics,
  type FacilityPowerSummaryAccordionKey,
} from '../../../utils/facilityPowerSummarySections';
import { resolveEnabledStepIds } from '../../../utils/resolveRequestFormSteps';
import { isMultilineRichHtmlFieldKey } from '../../../utils/richContent/multilineRichHtmlFields';
import { configurationListHeaderMetricsProps } from '../../../utils/subsectionHeaderMetrics';
import { resolveSummaryEffectiveEnabledStepIds } from '../../../utils/summaryEnabledStepIds';
import { withSummaryLocationFieldDisplay } from '../../../utils/summaryLocationFieldDisplay';
import { buildSummaryLocationRenderUnits } from '../../../utils/summaryLocationGrouping';
import { summaryRowDisplayParts } from '../../../utils/summaryRowDisplayParts';
import { resolveOrderedSummarySectionKeys } from '../../../utils/summarySectionOrder';
import { valueWithSavedUnit, withSavedUnit } from '../../dimensionUnitChoices';

import type { SummaryProps } from './useSummary';
import type {
  SummaryCardSource,
  SummaryCardRow,
} from '../../../summary/summaryCardSourceTypes';
import type { AppRole } from '../../../types/auth';
import type { CoolingSystemConfiguration } from '../../../types/coolingSystem';
import type { LargeUpsConfiguration } from '../../../types/largeUps';
import type { LiquidCoolingCduConfiguration } from '../../../types/liquidCoolingCdu';
import type { PowerDistributionConfiguration } from '../../../types/powerDistribution';
import type { RackConfiguration } from '../../../types/rack';
import type { SmallUpsConfiguration } from '../../../types/smallUps';

function SummarySubsectionHeading({
  subsection,
  first = false,
}: {
  subsection?: string;
  first?: boolean;
}) {
  const title = getSummarySubsectionDisplayTitle(subsection);
  if (!title) {
    return null;
  }
  return (
    <Text
      style={[styles.subsectionTitle, first && styles.subsectionTitleFirst]}
    >
      {title}
    </Text>
  );
}

type SummaryPermissionRow = {
  dataField: string;
  permissionSection: string;
  permissionField: string;
};

/** Caption fallback for Summary appendix — subsection label only (not step.subsection path). */
function supportingDocumentsAttachmentCaption(
  sectionId: string,
  stageLabel: string,
  groupKey: string,
): string {
  return formatGroupedSubstageLabel(sectionId, stageLabel, groupKey);
}

interface SummarySectionProps {
  title: string;
  filledCount: number;
  totalCount: number;
  countOnly?: boolean;
  isExpanded: boolean;
  onToggle: () => void;
  children: React.ReactNode;
  /** Web E2E: expand/collapse control for this section */
  headerTestID?: string;
}

const SummarySection: React.FC<SummarySectionProps> = ({
  title,
  filledCount,
  totalCount,
  countOnly,
  isExpanded,
  onToggle,
  children,
  headerTestID,
}) => (
  <View style={styles.summarySection}>
    <TouchableOpacity
      style={styles.sectionHeader}
      onPress={onToggle}
      testID={headerTestID}
    >
      <Text style={styles.sectionTitle}>{title}</Text>
      <View style={styles.sectionRight}>
        <View style={styles.countBadge}>
          <Text style={styles.countText}>
            {countOnly ? filledCount : `${filledCount}/${totalCount}`}
          </Text>
        </View>
        {isExpanded ? (
          <ChevronUp size={16} color="#666" style={styles.chevron} />
        ) : (
          <ChevronDown size={16} color="#666" style={styles.chevron} />
        )}
      </View>
    </TouchableOpacity>
    {isExpanded && <View style={styles.sectionContent}>{children}</View>}
  </View>
);

type SummaryMediaContextValue = {
  attachments: SavedAttachment[];
  attachmentsById: Map<number, SavedAttachment>;
  openCarousel: (attachment: SavedAttachment) => void;
  defaultHtmlSource: Record<string, unknown>;
  requestId?: string;
};

const SummaryMediaContext =
  React.createContext<SummaryMediaContextValue | null>(null);

const DataRow: React.FC<{
  label: string;
  value: string | number | boolean | null | undefined;
  testID?: string;
  isLastRow?: boolean;
  richHtml?: string;
}> = ({ label, value, testID, isLastRow = false, richHtml }) => {
  const media = React.useContext(SummaryMediaContext);
  const { displayText, isSpecified } = summaryRowDisplayParts(value);
  const showRich = Boolean(richHtml?.trim()) && media != null;
  return (
    <View
      style={[styles.dataRow, isLastRow && styles.dataRowLast]}
      testID={testID}
    >
      <View style={styles.dataLabelContainer}>
        <Text style={styles.dataLabel}>{label}:</Text>
      </View>
      <View style={styles.dataValueContainer}>
        {showRich && richHtml != null && media != null ? (
          <SummaryRichTextValue
            html={richHtml}
            requestId={media.requestId}
            testID={testID != null ? `${testID}-rich` : undefined}
          />
        ) : (
          <Text
            style={
              isSpecified ? styles.dataValue : styles.dataValueNotSpecified
            }
            testID={testID != null ? `${testID}-value` : undefined}
          >
            {displayText}
          </Text>
        )}
      </View>
    </View>
  );
};

interface PermissionedDataRowProps {
  section: string;
  field: string;
  dataField?: string;
  htmlSource?: Record<string, unknown>;
  label: string;
  value: string | number | boolean | null | undefined;
  getFieldPermissionSync: (section: string, field: string) => any;
  bypassPermissions?: boolean;
  /** Web E2E / parity: stable row handle (e.g. summary-site-walk-oppSiteAddress) */
  rowTestID?: string;
  isLastRow?: boolean;
  /**
   * Registry-driven (`SummaryCardRow.visibleWhenFieldContains`, evaluated by the
   * caller via `isSummaryRowGateSatisfied`): omit the row entirely when its gating
   * selection is absent. When the gate IS satisfied the row renders normally —
   * including "Not specified" for an unanswered value. Used by the conditional
   * Smart IT NVL configs, gated on their workload option (migration 092).
   *
   * Distinct from the permission-driven hide below: this is a content gate, not an
   * RBAC gate.
   */
  hidden?: boolean;
}

function resolveSummaryRowRichHtml(
  dataField: string | undefined,
  value: string | number | boolean | null | undefined,
  htmlSource: Record<string, unknown> | undefined,
): string | undefined {
  if (!dataField || !isMultilineRichHtmlFieldKey(dataField)) {
    return undefined;
  }
  const fromSource = htmlSource?.[dataField];
  if (typeof fromSource === 'string') {
    return fromSource;
  }
  if (
    typeof value === 'string' &&
    (value.includes('<img') || value.includes('sitescope-attachment'))
  ) {
    return value;
  }
  return undefined;
}

const PermissionedDataRow: React.FC<PermissionedDataRowProps> = ({
  section,
  field,
  dataField,
  htmlSource,
  label,
  value,
  getFieldPermissionSync,
  bypassPermissions = false,
  rowTestID,
  isLastRow = false,
  hidden = false,
}) => {
  const media = React.useContext(SummaryMediaContext);
  const richHtml = resolveSummaryRowRichHtml(
    dataField,
    value,
    htmlSource ?? media?.defaultHtmlSource,
  );

  if (hidden) {
    return null;
  }

  // If bypassing permissions, always render
  if (bypassPermissions) {
    return (
      <DataRow
        label={label}
        value={value}
        testID={rowTestID}
        isLastRow={isLastRow}
        richHtml={richHtml}
      />
    );
  }

  const access = getFieldPermissionSync(section, field);

  // #997 Bugs 17/18/19: the Summary is a read/review surface and fails OPEN on a
  // permission cache miss. `getFieldPermissionSync` returns null when the permission
  // is not yet hydrated (cold cache) — that is "unknown", not "deny". Rows have
  // already been gated synchronously by `filterSummaryRowsForFormView` (union-of-roles),
  // so on an unknown result we render rather than silently hide saved data. Only an
  // explicit deny (hidden / no read access) hides the row. Edit/write paths stay strict.
  if (access && (access.isHidden || !access.canRead)) {
    return null;
  }

  return (
    <DataRow
      label={label}
      value={value}
      testID={rowTestID}
      isLastRow={isLastRow}
      richHtml={richHtml}
    />
  );
};

export const Summary: React.FC<SummaryProps> = ({
  formData,
  request,
  editedData,
  isEditing = false,
  attachmentsBySection = {},
  requestId,
  isAdmin = false,
  navScopeSnapshot = null,
  dirtyFields,
}) => {
  const requestScopeFromContext = useRequestScope();
  const { formValues, requestId: metricsRequestId } = useSidebarStepMetrics();
  const { user } = useAuth();

  const requestScopeForSummary = useMemo(() => {
    const source = navScopeSnapshot ?? requestScopeFromContext;
    if (!source) {
      return null;
    }
    return {
      storedStepIds: source.storedStepIds,
      enabledStepIds: source.enabledStepIds,
      removableStepIds: source.removableStepIds,
    };
  }, [
    navScopeSnapshot,
    navScopeSnapshot?.storedStepIds,
    navScopeSnapshot?.enabledStepIds,
    navScopeSnapshot?.removableStepIds,
    requestScopeFromContext,
    requestScopeFromContext?.storedStepIds,
    requestScopeFromContext?.enabledStepIds,
    requestScopeFromContext?.removableStepIds,
  ]);

  // Use request data if available (view mode), otherwise use formData (create mode).
  // Opened-request edit mode: merge unsaved edits so Summary matches other steps (e.g. Racks additional info).
  const data = useMemo((): Record<string, unknown> => {
    const base = request ?? formData;
    if (!base) {
      return {};
    }
    if (!request || !isEditing || !editedData) {
      return base as Record<string, unknown>;
    }
    const merged: Record<string, unknown> = { ...base };
    for (const [key, value] of Object.entries(editedData)) {
      if (value !== undefined) {
        merged[key] = value;
      }
    }
    return merged;
  }, [request, formData, isEditing, editedData]);

  const labelFor = (row: { label: string; dataField: string }) =>
    withSavedUnit(row.label, row.dataField, data.dimensionUnits);

  // Memoize the preload sections to prevent re-renders
  const preloadSections = useMemo(
    () => [
      'Opportunity Information',
      'Site Dimensions',
      'Facility Power Readiness',
      'Racks',
      'Rack Power',
      'Small UPS',
      'Cooling Readiness',
      'Liquid Cooling',
      'Customer IT Equipment',
      'Supporting Documentation',
    ],
    [],
  );

  // Memoize the request stage
  const requestStage = useMemo(
    () => String(data?.stage || 'New'),
    [data?.stage],
  );

  // #997 Bugs 3/17/18/19: the Summary is a read/review surface and always resolves
  // field visibility from the union of the user's roles at the current stage (decision A —
  // `union_all`). It intentionally forces `showFullDetails: true` so it does NOT apply
  // the sidebar basic/advanced density collapse, which `resolveEffectiveFormViewPermissionContext`
  // would otherwise use to drop fields multi-role users are allowed to read. This keeps the
  // Summary field set in parity with the editor without touching the shared editor collapse
  // (`formFieldVisibility.ts` / `SimplePermissionWrapper.tsx`).
  const fieldVisibility = useMemo(
    () => ({
      showFullDetails: true,
      requestStage,
      requestId: metricsRequestId ?? requestId ?? undefined,
      userRoles: (user?.roles ?? []) as AppRole[],
      userEmail: user?.email,
    }),
    [requestStage, metricsRequestId, requestId, user?.roles, user?.email],
  );

  // Initialize field permissions with preloading for all sections
  const { getFieldPermissionSync, clearCache, preloadPermissions } =
    useFieldPermissions({
      preloadSections,
      enableCaching: true,
      requestStage,
      autoRefresh: true, // Re-enable autoRefresh to test with debugging
    });

  // Clear cache and reload permissions when request stage changes
  React.useEffect(() => {
    if (requestStage) {
      clearCache();
      preloadPermissions();
    }
  }, [requestStage, clearCache, preloadPermissions]);

  // Try with autoRefresh enabled now that we have debugging
  const storedStepIdsForAttachments = useMemo(() => {
    if (requestScopeForSummary?.storedStepIds != null) {
      return requestScopeForSummary.storedStepIds;
    }
    if (isEditing && editedData?.enabledStepIds !== undefined) {
      return editedData.enabledStepIds as string[] | null;
    }
    if (formData?.enabledStepIds !== undefined) {
      return formData.enabledStepIds as string[] | null;
    }
    return resolveStoredStepIdsForAttachments({
      requestScopeStoredStepIds: requestScopeForSummary?.storedStepIds,
      requestEnabledStepIds: request?.enabledStepIds,
    });
  }, [
    requestScopeForSummary?.storedStepIds,
    isEditing,
    editedData?.enabledStepIds,
    formData?.enabledStepIds,
    request?.enabledStepIds,
  ]);

  const attachmentScopeOptions = useMemo(
    () =>
      resolveAttachmentScopeOptions({
        storedStepIds: storedStepIdsForAttachments,
        requestStage,
      }),
    [storedStepIdsForAttachments, requestStage],
  );
  const bypassPermissions = false;

  // Aggregate attachments by nav-aligned display group (#1330 facility-power family split).
  const attachmentsByStage = useMemo(() => {
    const uploadStyleAttachmentsBySection =
      filterInlineAttachmentsFromSectionMap(attachmentsBySection ?? {});

    debugAttachmentGrouping('Summary.attachmentsByStage.start', {
      sectionCounts: summarizeSectionAttachmentCounts(
        uploadStyleAttachmentsBySection,
      ),
      enabledStepIds: attachmentScopeOptions.enabledStepIds,
      storedStepIds: attachmentScopeOptions.storedStepIds ?? null,
    });

    const grouped = buildGroupedAttachmentsFromSectionMap(
      uploadStyleAttachmentsBySection,
      attachmentScopeOptions,
    );

    const result = grouped.map(group => ({
      stage: group.stage,
      label: group.stageLabel,
      items: group.substages.flatMap(substage =>
        (substage.attachments ?? []).map(attachment => ({
          sectionId: substage.sectionId,
          sectionLabel: substage.sectionLabel,
          attachment,
        })),
      ),
    }));

    if (grouped.length > 0 && result.length === 0) {
      debugAttachmentGrouping('Summary.attachmentsByStage.filteredEmpty', {
        grouped: summarizeGroupedAttachments(grouped),
        reason: 'grouped rows had no renderable items after flatten',
      });
    }

    debugAttachmentGrouping('Summary.attachmentsByStage.result', {
      grouped: summarizeGroupedAttachments(grouped),
      stageCount: result.length,
      itemCount: result.reduce((sum, group) => sum + group.items.length, 0),
      stageLabels: result.map(group => group.label),
    });

    return result;
  }, [attachmentsBySection, attachmentScopeOptions]);

  // L3: Simple derivation from already-memoized attachmentsByStage (max 12 groups)
  const totalAttachmentCount = attachmentsByStage.reduce(
    (sum, group) => sum + group.items.length,
    0,
  );

  const { carouselProps, openCarousel } = useAttachmentCarousel();

  const summaryAttachments = useMemo(() => {
    const attachmentsById = new Map<number, SavedAttachment>();
    for (const group of attachmentsByStage) {
      for (const item of group.items) {
        if (item.attachment.id != null) {
          attachmentsById.set(item.attachment.id, item.attachment);
        }
      }
    }
    return {
      attachments: [...attachmentsById.values()],
      attachmentsById,
    };
  }, [attachmentsByStage]);

  const summaryMedia = useMemo((): SummaryMediaContextValue => {
    const { attachments, attachmentsById } = summaryAttachments;
    const resolvedRequestId =
      request?.id != null
        ? String(request.id)
        : requestId != null
        ? String(requestId)
        : undefined;
    return {
      attachments,
      attachmentsById,
      openCarousel: tapped => {
        openCarousel(
          tapped,
          attachments,
          getSectionLabel(tapped.requestSection ?? ''),
        );
      },
      defaultHtmlSource: data,
      requestId: resolvedRequestId,
    };
  }, [summaryAttachments, openCarousel, data, request?.id, requestId]);

  useEffect(() => {
    registerAttachmentScopeDebugGlobals();
    registerAttachmentGroupingDebugGlobals();
    debugAttachmentScope('Summary.supportingDocuments', {
      requestId: request?.id ?? requestId ?? null,
      requestEnabledStepIds: request?.enabledStepIds ?? null,
      requestScopeStoredStepIds: requestScopeForSummary?.storedStepIds ?? null,
      storedStepIdsForAttachments,
      enabledStepIds: attachmentScopeOptions.enabledStepIds,
      attachmentsBySection,
      attachmentsByStageCount: attachmentsByStage.length,
      totalAttachmentCount,
      stageLabels: attachmentsByStage.map(g => g.label),
    });
  }, [
    request?.id,
    request?.enabledStepIds,
    requestId,
    requestScopeForSummary?.storedStepIds,
    storedStepIdsForAttachments,
    attachmentScopeOptions.enabledStepIds,
    attachmentsBySection,
    attachmentsByStage,
    totalAttachmentCount,
  ]);

  // Get screen dimensions for responsive layout
  const { width } = Dimensions.get('window');
  const isSmallScreen = width < 768;
  const persistedRequestId =
    request?.id != null && !String(request.id).startsWith('o-')
      ? String(request.id)
      : undefined;
  const { data: persistedRacks = [] } = useRackConfigurations(
    persistedRequestId ?? '',
    Boolean(persistedRequestId),
  );
  const { data: persistedLargeUps = [] } = useLargeUpsConfigurations(
    persistedRequestId ?? '',
    Boolean(persistedRequestId),
  );
  const { data: persistedPowerDistribution = [] } =
    usePowerDistributionConfigurations(
      persistedRequestId ?? '',
      Boolean(persistedRequestId),
    );
  const { data: persistedSmallUps = [] } = useSmallUpsConfigurations(
    persistedRequestId ?? '',
    Boolean(persistedRequestId),
  );
  const { data: persistedCoolingSystems = [] } = useCoolingSystemConfigurations(
    persistedRequestId ?? '',
    Boolean(persistedRequestId),
  );
  const { data: persistedLiquidCoolingCdus = [] } =
    useLiquidCoolingCduConfigurations(
      persistedRequestId ?? '',
      Boolean(persistedRequestId),
    );
  const draftRacks = (formData?.rackConfigurations ??
    data.rackConfigurations) as RackConfiguration[] | undefined;
  const draftLargeUps = (formData?.largeUpsConfigurations ??
    data.largeUpsConfigurations) as LargeUpsConfiguration[] | undefined;
  const draftPowerDistribution = (formData?.powerDistributionConfigurations ??
    data.powerDistributionConfigurations) as
    | PowerDistributionConfiguration[]
    | undefined;
  const draftSmallUps = (formData?.smallUpsConfigurations ??
    data.smallUpsConfigurations) as SmallUpsConfiguration[] | undefined;
  const draftCoolingSystems = (formData?.coolingSystemConfigurations ??
    data.coolingSystemConfigurations) as
    | CoolingSystemConfiguration[]
    | undefined;
  const draftLiquidCoolingCdus = (formData?.liquidCoolingCduConfigurations ??
    data.liquidCoolingCduConfigurations) as
    | LiquidCoolingCduConfiguration[]
    | undefined;
  // #997 Story 6.3 Phase 4b/6: the live rack edit session rides
  // editedData.rackConfigurations (already merged into `data` → draftRacks),
  // so no separate session binding is overlaid here.
  const summaryRacks = useMemo(
    () =>
      resolveSummaryRackConfigurations({
        draftRacks,
        persistedRacks,
        edited: dirtyFields?.has('rackConfigurations') ?? false,
      }),
    [draftRacks, persistedRacks, dirtyFields],
  );
  // #997 Story 6.5: the live Large UPS edit session rides
  // editedData.largeUpsConfigurations (already merged into `data` → draftLargeUps),
  // so no separate session binding is overlaid here (mirror racks / small UPS).
  const summaryLargeUps = useMemo(
    () =>
      resolveSummaryLargeUpsConfigurations({
        draftUnits: draftLargeUps,
        persistedUnits: persistedLargeUps,
        edited: dirtyFields?.has('largeUpsConfigurations') ?? false,
      }),
    [draftLargeUps, persistedLargeUps, dirtyFields],
  );
  // #997 Story 6.7: the live Power Distribution edit session rides
  // editedData.powerDistributionConfigurations (already merged into `data` →
  // draftPowerDistribution), so no separate session binding is overlaid here
  // (mirror racks / small UPS / large UPS / cooling).
  const summaryPowerDistribution = useMemo(
    () =>
      resolveSummaryPowerDistributionConfigurations({
        draftUnits: draftPowerDistribution,
        persistedUnits: persistedPowerDistribution,
        edited: dirtyFields?.has('powerDistributionConfigurations') ?? false,
      }),
    [draftPowerDistribution, persistedPowerDistribution, dirtyFields],
  );
  // #997 Story 6.4 Phase 6: the live Small UPS edit session rides
  // editedData.smallUpsConfigurations (already merged into `data` → draftSmallUps),
  // so no separate session binding is overlaid here (mirror racks).
  const summarySmallUps = useMemo(
    () =>
      resolveSummarySmallUpsConfigurations({
        draftUnits: draftSmallUps,
        persistedUnits: persistedSmallUps,
        edited: dirtyFields?.has('smallUpsConfigurations') ?? false,
      }),
    [draftSmallUps, persistedSmallUps, dirtyFields],
  );
  // #997 Story 6.6: the live Cooling edit session rides
  // editedData.coolingSystemConfigurations (already merged into `data` →
  // draftCoolingSystems), so no separate session binding is overlaid here
  // (mirror racks / small UPS / large UPS).
  const summaryCoolingSystems = useMemo(
    () =>
      resolveSummaryCoolingSystemConfigurations({
        draftUnits: draftCoolingSystems,
        persistedUnits: persistedCoolingSystems,
        edited: dirtyFields?.has('coolingSystemConfigurations') ?? false,
      }),
    [draftCoolingSystems, persistedCoolingSystems, dirtyFields],
  );
  // #997 Story 6.8: the live Liquid Cooling edit session rides
  // editedData.liquidCoolingCduConfigurations (already merged into `data` →
  // draftLiquidCoolingCdus), so no separate session binding is overlaid here
  // (mirror racks / small UPS / large UPS / cooling / power distribution).
  const summaryLiquidCoolingCdus = useMemo(
    () =>
      resolveSummaryLiquidCoolingCduConfigurations({
        draftUnits: draftLiquidCoolingCdus,
        persistedUnits: persistedLiquidCoolingCdus,
        edited: dirtyFields?.has('liquidCoolingCduConfigurations') ?? false,
      }),
    [draftLiquidCoolingCdus, persistedLiquidCoolingCdus, dirtyFields],
  );

  // #999 Epic 4.1 — location-first ordering on Summary.
  // Persisted/offline requests resolve server/offline tags; Create Request uses
  // the draft context. Per-unit Location rows are the single visible location
  // value; group headers are omitted to avoid repeating the same information.
  const locationTagRequestId =
    request?.id != null
      ? String(request.id)
      : formData?.id != null
      ? String(formData.id)
      : 'new-request';
  const {
    tags: summaryLocationTags,
    resolveName: resolveSummaryLocationTagName,
  } = useLocationTagNameLookup(locationTagRequestId);
  /**
   * Union of form/request scope + sidebar live state + field progress (#1330).
   * #997 Bug 19: saved CDU units force `liquid-cooling` on so the per-CDU summary
   * renders even when `enabledStepIds` omits it (defined after the CDU memo so the
   * config count is in scope).
   */
  const effectiveEnabledStepIds = useMemo((): RequestFormStepId[] => {
    // #1501: with aggregate save, Add/Remove Scope stays in the edit session
    // until Save — the persisted `request.enabledStepIds` still lists a scope
    // the user just removed, and its cached units are not purged yet. Read the
    // session scope while editing so Summary matches the sidebar.
    const sessionStepIds =
      request && isEditing && editedData?.enabledStepIds !== undefined
        ? (editedData.enabledStepIds as string[] | null)
        : undefined;
    const requestForScope =
      sessionStepIds !== undefined && request
        ? { ...request, enabledStepIds: sessionStepIds }
        : request;
    const liquidCoolingRemovedInSession =
      sessionStepIds !== undefined &&
      !resolveEnabledStepIds(sessionStepIds).includes('liquid-cooling');
    const liquidCoolingCduCount = liquidCoolingRemovedInSession
      ? 0
      : summaryLiquidCoolingCdus.length;
    if (requestScopeForSummary) {
      return resolveSummaryEffectiveEnabledStepIds({
        formData,
        formValues,
        request: requestForScope,
        requestScope: requestScopeForSummary,
        liquidCoolingCduCount,
      });
    }
    return resolveSummaryEffectiveEnabledStepIds({
      formData,
      request: requestForScope,
      editedData,
      isEditing,
      formValues,
      requestScope: null,
      liquidCoolingCduCount,
    });
  }, [
    formData,
    formData?.enabledStepIds,
    request,
    request?.enabledStepIds,
    editedData,
    editedData?.enabledStepIds,
    isEditing,
    formValues,
    formValues?.enabledStepIds,
    requestScopeForSummary,
    requestScopeForSummary?.storedStepIds,
    requestScopeForSummary?.enabledStepIds,
    requestScopeForSummary?.removableStepIds,
    summaryLiquidCoolingCdus.length,
  ]);
  const perRackSummaryRows = useMemo(() => getPerRackSummaryDefinitions(), []);
  const perLargeUpsSummaryRows = useMemo(
    () => getPerLargeUpsSummaryDefinitions(),
    [],
  );
  const perPowerDistributionSummaryRows = useMemo(
    () => getPerPowerDistributionSummaryDefinitions(),
    [],
  );
  const perSmallUpsSummaryRows = useMemo(
    () => getPerSmallUpsSummaryDefinitions(),
    [],
  );
  const perCoolingSystemSummaryRows = useMemo(
    () => getPerCoolingSystemSummaryDefinitions(),
    [],
  );
  const coolingSharedSummaryRows = useMemo(
    () => getCoolingSharedSummaryDefinitions(),
    [],
  );
  const perLiquidCoolingCduSummaryRows = useMemo(
    () => getPerLiquidCoolingCduSummaryDefinitions(),
    [],
  );
  const liquidCoolingSharedSummaryRows = useMemo(
    () => getLiquidCoolingSharedSummaryDefinitions(),
    [],
  );
  const displayLargeUpsUnits = useMemo(
    () => withRepeatableSummaryPlaceholderUnit(summaryLargeUps),
    [summaryLargeUps],
  );
  const displayPowerDistributionUnits = useMemo(
    () => withRepeatableSummaryPlaceholderUnit(summaryPowerDistribution),
    [summaryPowerDistribution],
  );
  const displaySmallUpsUnits = useMemo(
    () => withRepeatableSummaryPlaceholderUnit(summarySmallUps),
    [summarySmallUps],
  );
  const displayCoolingSystemUnits = useMemo(
    () => withRepeatableSummaryPlaceholderUnit(summaryCoolingSystems),
    [summaryCoolingSystems],
  );
  const displayLiquidCoolingCduUnits = useMemo(
    () => withRepeatableSummaryPlaceholderUnit(summaryLiquidCoolingCdus),
    [summaryLiquidCoolingCdus],
  );
  const useEditorLargeUpsSummary =
    effectiveEnabledStepIds.includes('large-ups');
  const useEditorPowerDistributionSummary =
    effectiveEnabledStepIds.includes('power-distribution');
  const useEditorSmallUpsSummary =
    effectiveEnabledStepIds.includes('small-ups');
  const useEditorCoolingSystemSummary =
    effectiveEnabledStepIds.includes('cooling-readiness');
  const useEditorLiquidCoolingCduSummary =
    effectiveEnabledStepIds.includes('liquid-cooling');
  const useMultiRackSummary = summaryRacks.length > 0;
  const visiblePerRackRows = useMemo(
    () => filterSummaryRowsForFormView(perRackSummaryRows, fieldVisibility),
    [perRackSummaryRows, fieldVisibility],
  );
  const visiblePerLargeUpsRows = useMemo(
    () => filterSummaryRowsForFormView(perLargeUpsSummaryRows, fieldVisibility),
    [perLargeUpsSummaryRows, fieldVisibility],
  );
  const visiblePerPowerDistributionRows = useMemo(
    () =>
      filterSummaryRowsForFormView(
        perPowerDistributionSummaryRows,
        fieldVisibility,
      ),
    [perPowerDistributionSummaryRows, fieldVisibility],
  );
  const visiblePerSmallUpsRows = useMemo(
    () => filterSummaryRowsForFormView(perSmallUpsSummaryRows, fieldVisibility),
    [perSmallUpsSummaryRows, fieldVisibility],
  );
  const visiblePerCoolingSystemRows = useMemo(
    () =>
      filterSummaryRowsForFormView(
        perCoolingSystemSummaryRows,
        fieldVisibility,
      ),
    [perCoolingSystemSummaryRows, fieldVisibility],
  );
  const visibleCoolingSharedRows = useMemo(
    () =>
      filterSummaryRowsForFormView(coolingSharedSummaryRows, fieldVisibility),
    [coolingSharedSummaryRows, fieldVisibility],
  );
  const visiblePerLiquidCoolingCduRows = useMemo(
    () =>
      filterSummaryRowsForFormView(
        perLiquidCoolingCduSummaryRows,
        fieldVisibility,
      ),
    [perLiquidCoolingCduSummaryRows, fieldVisibility],
  );
  const visibleLiquidCoolingSharedRows = useMemo(
    () =>
      filterSummaryRowsForFormView(
        liquidCoolingSharedSummaryRows,
        fieldVisibility,
      ),
    [liquidCoolingSharedSummaryRows, fieldVisibility],
  );
  const rackEditorMetricFields = useMemo(
    () =>
      RACK_FORM_SUMMARY_FIELD_ORDER.map(field => ({
        key: field.dataField,
        shortLabel: field.shortLabel ?? field.dataField,
      })),
    [],
  );
  const multiRackMetrics = useMemo(() => {
    if (!useMultiRackSummary) {
      return { filled: 0, total: 0 };
    }
    const metrics = configurationListHeaderMetricsProps(
      rackEditorMetricFields,
      summaryRacks as unknown as Record<string, unknown>[],
      { visibility: fieldVisibility },
    );
    return {
      filled: metrics.fieldFillCount,
      total: metrics.fieldTotalCount,
    };
  }, [
    useMultiRackSummary,
    rackEditorMetricFields,
    summaryRacks,
    fieldVisibility,
  ]);
  const multiRackFilledCount = multiRackMetrics.filled;
  const multiRackTotalCount = multiRackMetrics.total;
  const multiSmallUpsMetrics = useMemo(() => {
    if (!useEditorSmallUpsSummary) {
      return { filled: 0, total: 0 };
    }
    const metrics = configurationListHeaderMetricsProps(
      SMALL_UPS_SUMMARY_METRIC_FIELDS,
      displaySmallUpsUnits as unknown as Record<string, unknown>[],
      { visibility: fieldVisibility },
    );
    return {
      filled: metrics.fieldFillCount,
      total: metrics.fieldTotalCount,
    };
  }, [useEditorSmallUpsSummary, displaySmallUpsUnits, fieldVisibility]);
  const multiSmallUpsFilledCount = multiSmallUpsMetrics.filled;
  const multiSmallUpsTotalCount = multiSmallUpsMetrics.total;
  const multiCoolingSystemMetrics = useMemo(() => {
    if (!useEditorCoolingSystemSummary) {
      return { filled: 0, total: 0 };
    }
    const metrics = configurationListHeaderMetricsProps(
      COOLING_SYSTEM_SUMMARY_METRIC_FIELDS,
      displayCoolingSystemUnits as unknown as Record<string, unknown>[],
      {
        sharedValues: data as Record<string, unknown>,
        sharedFieldKeys: COOLING_EDITOR_SHARED_FIELD_KEYS,
        visibility: fieldVisibility,
      },
    );
    return {
      filled: metrics.fieldFillCount,
      total: metrics.fieldTotalCount,
    };
  }, [
    useEditorCoolingSystemSummary,
    displayCoolingSystemUnits,
    data,
    fieldVisibility,
  ]);
  const multiCoolingSystemFilledCount = multiCoolingSystemMetrics.filled;
  const multiCoolingSystemTotalCount = multiCoolingSystemMetrics.total;
  const multiLiquidCoolingCduMetrics = useMemo(() => {
    if (!useEditorLiquidCoolingCduSummary) {
      return { filled: 0, total: 0 };
    }
    const metrics = configurationListHeaderMetricsProps(
      LIQUID_COOLING_CDU_SUMMARY_METRIC_FIELDS,
      displayLiquidCoolingCduUnits as unknown as Record<string, unknown>[],
      {
        sharedValues: data as Record<string, unknown>,
        sharedFieldKeys: LIQUID_COOLING_CDU_EDITOR_SHARED_FIELD_KEYS,
        visibility: fieldVisibility,
      },
    );
    return {
      filled: metrics.fieldFillCount,
      total: metrics.fieldTotalCount,
    };
  }, [
    useEditorLiquidCoolingCduSummary,
    displayLiquidCoolingCduUnits,
    data,
    fieldVisibility,
  ]);
  const multiLiquidCoolingCduFilledCount = multiLiquidCoolingCduMetrics.filled;
  const multiLiquidCoolingCduTotalCount = multiLiquidCoolingCduMetrics.total;

  const [expandedSections, setExpandedSections] = useState<{
    [key: string]: boolean;
  }>({
    opportunity: false,
    siteDimensions: false,
    racks: false,
    facilityPower: false,
    largeUps: false,
    powerDistribution: false,
    rackPower: false,
    smallUps: false,
    coolingReadiness: false,
    liquidCooling: false,
    customerItEquipment: false,
    supportingDocuments: false,
  });

  const toggleSection = (section: string) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section],
    }));
  };

  // Check if all sections are expanded
  const allSectionsExpanded = Object.values(expandedSections).every(
    isExpanded => isExpanded,
  );

  // Expand all sections
  const expandAllSections = () => {
    setExpandedSections(prev => {
      const newState = { ...prev };
      Object.keys(newState).forEach(key => {
        newState[key] = true;
      });
      return newState;
    });
  };

  // Collapse all sections
  const collapseAllSections = () => {
    setExpandedSections(prev => {
      const newState = { ...prev };
      Object.keys(newState).forEach(key => {
        newState[key] = false;
      });
      return newState;
    });
  };

  // Memoize Summary card data from the unified registry (one contract per card).
  const sm = useMemo(() => {
    const pack = (source: SummaryCardSource) => ({
      rows: source.getRowDefinitions(),
      fields: source.getFieldMappings(),
      value: source.rowValue,
    });
    const S = SUMMARY_CARD_SOURCES;
    return {
      opportunity: pack(S.opportunity),
      siteInformation: pack(S.siteInformation),
      racks: pack(S.racks),
      facilityPower: pack(S.facilityPower),
      smallUps: pack(S.smallUps),
      coolingReadiness: pack(S.coolingReadiness),
      liquidCooling: pack(S.liquidCooling),
    };
  }, []);

  const sectionMetrics = useMemo(() => {
    const values = data as Record<string, unknown>;
    const pack = (rows: readonly SummaryPermissionRow[]) =>
      resolveSummarySectionMetrics(rows, values, fieldVisibility);
    return {
      opportunity: pack(sm.opportunity.rows),
      siteInformation: pack(sm.siteInformation.rows),
      racks: pack(sm.racks.rows),
      smallUps: pack(sm.smallUps.rows),
      coolingReadiness: pack(sm.coolingReadiness.rows),
      liquidCooling: pack(sm.liquidCooling.rows),
    };
  }, [sm, data, fieldVisibility]);

  const lastOpportunityRow =
    sectionMetrics.opportunity.visibleRows.at(-1)?.dataField;
  const lastSiteInformationRow =
    sectionMetrics.siteInformation.visibleRows.at(-1)?.dataField;
  const lastRacksRow = sectionMetrics.racks.visibleRows.at(-1)?.dataField;
  const renderFacilityPowerSummaryAccordion = (
    accordionKey: FacilityPowerSummaryAccordionKey,
  ) => {
    const flatMetrics = resolveFacilityPowerSummaryAccordionMetrics({
      accordionKey,
      allRows: sm.facilityPower.rows,
      enabledStepIds: effectiveEnabledStepIds,
      storedStepIds: requestScopeForSummary?.storedStepIds,
      stage: requestStage,
      data: data as Record<string, unknown>,
      visibility: fieldVisibility,
    });
    const { isPowerDistributionStageGated } = flatMetrics;
    const useMultiUnitSummary =
      (accordionKey === 'largeUps' && useEditorLargeUpsSummary) ||
      (accordionKey === 'powerDistribution' &&
        useEditorPowerDistributionSummary);

    const displayLargeUpsUnitsForAccordion = displayLargeUpsUnits;
    const displayPowerDistributionUnitsForAccordion =
      displayPowerDistributionUnits;

    const perInstanceFieldSet = new Set(
      accordionKey === 'largeUps'
        ? visiblePerLargeUpsRows.map(row => row.dataField)
        : accordionKey === 'powerDistribution'
        ? visiblePerPowerDistributionRows.map(row => row.dataField)
        : [],
    );

    let displayRows = flatMetrics.displayRows;
    let filled = flatMetrics.filled;
    let total = flatMetrics.total;

    if (accordionKey === 'largeUps' && useEditorLargeUpsSummary) {
      displayRows = flatMetrics.displayRows.filter(
        row => !perInstanceFieldSet.has(row.dataField),
      );
      const editorMetrics = configurationListHeaderMetricsProps(
        LARGE_UPS_SUMMARY_METRIC_FIELDS,
        displayLargeUpsUnitsForAccordion as unknown as Record<
          string,
          unknown
        >[],
        { visibility: fieldVisibility },
      );
      filled = editorMetrics.fieldFillCount;
      total = editorMetrics.fieldTotalCount;
    } else if (
      accordionKey === 'powerDistribution' &&
      useEditorPowerDistributionSummary
    ) {
      displayRows = flatMetrics.displayRows.filter(
        row => !perInstanceFieldSet.has(row.dataField),
      );
      const editorMetrics = configurationListHeaderMetricsProps(
        POWER_DISTRIBUTION_SUMMARY_METRIC_FIELDS,
        displayPowerDistributionUnitsForAccordion as unknown as Record<
          string,
          unknown
        >[],
        { visibility: fieldVisibility },
      );
      filled = editorMetrics.fieldFillCount;
      total = editorMetrics.fieldTotalCount;
    }

    const lastFlatRow = displayRows.at(-1)?.dataField;
    const lastPerLargeUpsField =
      visiblePerLargeUpsRows[visiblePerLargeUpsRows.length - 1]?.dataField;
    const lastPerPowerDistributionField =
      visiblePerPowerDistributionRows[
        visiblePerPowerDistributionRows.length - 1
      ]?.dataField;
    const headerTestId =
      accordionKey === 'facilityPower'
        ? 'summary-section-header-facility-power-readiness'
        : accordionKey === 'largeUps'
        ? 'summary-section-header-large-ups'
        : 'summary-section-header-power-distribution';
    const rowTestIdPrefix =
      accordionKey === 'facilityPower'
        ? 'summary-facility-power-row-'
        : accordionKey === 'largeUps'
        ? 'summary-large-ups-row-'
        : 'summary-power-distribution-row-';

    const renderFlatRow = (
      row: SummaryCardRow,
      index: number,
      options?: { isLastRow?: boolean; keyPrefix?: string },
    ) => (
      <React.Fragment key={`${options?.keyPrefix ?? ''}${row.dataField}`}>
        <SummarySubsectionHeading
          subsection={row.subsection}
          first={index === 0}
        />
        <PermissionedDataRow
          section={row.permissionSection}
          field={row.permissionField}
          dataField={row.dataField}
          label={
            row.dataField === 'powerAdditionalInformation'
              ? resolvePowerAdditionalInformationSummaryLabel(accordionKey)
              : labelFor(row)
          }
          value={sm.facilityPower.value(
            data as Record<string, unknown>,
            row.dataField,
          )}
          getFieldPermissionSync={getFieldPermissionSync}
          bypassPermissions={bypassPermissions}
          rowTestID={`${rowTestIdPrefix}${row.dataField}`}
          isLastRow={options?.isLastRow ?? row.dataField === lastFlatRow}
        />
      </React.Fragment>
    );

    return (
      <SummarySection
        key={accordionKey}
        title={FACILITY_POWER_SUMMARY_ACCORDION_TITLES[accordionKey]}
        headerTestID={headerTestId}
        filledCount={filled}
        totalCount={total}
        isExpanded={expandedSections[accordionKey]}
        onToggle={() => toggleSection(accordionKey)}
      >
        {isPowerDistributionStageGated ? (
          <Text style={styles.stageGateNotice}>
            {POWER_DISTRIBUTION_STAGE_GATE_MESSAGE}
          </Text>
        ) : useMultiUnitSummary && accordionKey === 'largeUps' ? (
          <>
            {displayRows.map((row, index) => renderFlatRow(row, index))}
            {buildSummaryLocationRenderUnits({
              tags: summaryLocationTags,
              units: displayLargeUpsUnitsForAccordion,
              getLocationTagId: unit => unit.locationTagId,
              getStatus: unit => unit.status,
              getDisplayOrder: unit => unit.displayOrder,
            }).map(({ unit }, unitIndex) => (
              <React.Fragment
                key={
                  unit.id != null
                    ? String(unit.id)
                    : `draft-large-ups-${unitIndex}`
                }
              >
                <View style={unitIndex > 0 ? styles.rackUnitBlock : undefined}>
                  {visiblePerLargeUpsRows.map((row, rowIndex) => (
                    <React.Fragment key={`${unitIndex}-${row.dataField}`}>
                      <SummarySubsectionHeading
                        subsection={row.subsection}
                        first={
                          unitIndex === 0 &&
                          rowIndex === 0 &&
                          displayRows.length === 0
                        }
                      />
                      <PermissionedDataRow
                        section={row.permissionSection}
                        field={row.permissionField}
                        dataField={row.dataField}
                        htmlSource={unit as unknown as Record<string, unknown>}
                        label={labelFor(row)}
                        value={withSummaryLocationFieldDisplay(
                          row.dataField,
                          largeUpsSummaryUnitRowValue(unit, row.dataField),
                          resolveSummaryLocationTagName,
                        )}
                        getFieldPermissionSync={getFieldPermissionSync}
                        bypassPermissions={bypassPermissions}
                        rowTestID={`summary-large-ups-unit-${unitIndex}-row-${row.dataField}`}
                        isLastRow={row.dataField === lastPerLargeUpsField}
                      />
                    </React.Fragment>
                  ))}
                </View>
              </React.Fragment>
            ))}
          </>
        ) : useMultiUnitSummary && accordionKey === 'powerDistribution' ? (
          <>
            {displayRows.map((row, index) => renderFlatRow(row, index))}
            {buildSummaryLocationRenderUnits({
              tags: summaryLocationTags,
              units: displayPowerDistributionUnitsForAccordion,
              getLocationTagId: unit => unit.locationTagId,
              getStatus: unit => unit.status,
              getDisplayOrder: unit => unit.displayOrder,
            }).map(({ unit }, unitIndex) => (
              <React.Fragment
                key={
                  unit.id != null
                    ? String(unit.id)
                    : `draft-power-distribution-${unitIndex}`
                }
              >
                <View style={unitIndex > 0 ? styles.rackUnitBlock : undefined}>
                  {visiblePerPowerDistributionRows.map((row, rowIndex) => (
                    <React.Fragment key={`${unitIndex}-${row.dataField}`}>
                      <SummarySubsectionHeading
                        subsection={row.subsection}
                        first={
                          unitIndex === 0 &&
                          rowIndex === 0 &&
                          displayRows.length === 0
                        }
                      />
                      <PermissionedDataRow
                        section={row.permissionSection}
                        field={row.permissionField}
                        dataField={row.dataField}
                        htmlSource={unit as unknown as Record<string, unknown>}
                        label={withSavedUnit(
                          row.label,
                          row.dataField,
                          data.dimensionUnits,
                          unit.id,
                        )}
                        value={valueWithSavedUnit(
                          withSummaryLocationFieldDisplay(
                            row.dataField,
                            powerDistributionSummaryUnitRowValue(
                              unit,
                              row.dataField,
                            ),
                            resolveSummaryLocationTagName,
                          ),
                          row.dataField,
                          data.dimensionUnits,
                          unit.id,
                        )}
                        getFieldPermissionSync={getFieldPermissionSync}
                        bypassPermissions={bypassPermissions}
                        rowTestID={`summary-power-distribution-unit-${unitIndex}-row-${row.dataField}`}
                        isLastRow={
                          row.dataField === lastPerPowerDistributionField
                        }
                      />
                    </React.Fragment>
                  ))}
                </View>
              </React.Fragment>
            ))}
          </>
        ) : (
          displayRows
            .filter((row): row is SummaryCardRow => row != null)
            .map((row, index) => renderFlatRow(row, index))
        )}
      </SummarySection>
    );
  };

  const lastSmallUpsRow = sectionMetrics.smallUps.visibleRows.at(-1)?.dataField;
  const lastCoolingReadinessRow =
    sectionMetrics.coolingReadiness.visibleRows.at(-1)?.dataField;
  const lastLiquidCoolingRow =
    sectionMetrics.liquidCooling.visibleRows.at(-1)?.dataField;
  const lastPerRackSummaryField =
    visiblePerRackRows[visiblePerRackRows.length - 1]?.dataField;
  const lastPerSmallUpsField =
    visiblePerSmallUpsRows[visiblePerSmallUpsRows.length - 1]?.dataField;
  const lastPerCoolingSystemField =
    visiblePerCoolingSystemRows[visiblePerCoolingSystemRows.length - 1]
      ?.dataField;
  const lastPerLiquidCoolingCduField =
    visiblePerLiquidCoolingCduRows[visiblePerLiquidCoolingCduRows.length - 1]
      ?.dataField;
  const lastCoolingSharedField =
    visibleCoolingSharedRows[visibleCoolingSharedRows.length - 1]?.dataField;
  const lastLiquidCoolingSharedField =
    visibleLiquidCoolingSharedRows[visibleLiquidCoolingSharedRows.length - 1]
      ?.dataField;

  const orderedSummarySectionKeys = useMemo(
    () => resolveOrderedSummarySectionKeys(effectiveEnabledStepIds),
    [effectiveEnabledStepIds],
  );

  const renderSummarySectionByKey = (sectionKey: string): React.ReactNode => {
    switch (sectionKey) {
      case 'opportunity':
        return (
          <SummarySection
            title="Opportunity"
            headerTestID="summary-section-header-opportunity"
            filledCount={sectionMetrics.opportunity.filled}
            totalCount={sectionMetrics.opportunity.total}
            isExpanded={expandedSections.opportunity}
            onToggle={() => toggleSection('opportunity')}
          >
            {sectionMetrics.opportunity.visibleRows.map(
              (
                {
                  dataField,
                  permissionSection,
                  permissionField,
                  label,
                  subsection,
                  visibleWhenFieldContains,
                },
                index,
              ) => (
                <React.Fragment key={dataField}>
                  <SummarySubsectionHeading
                    subsection={subsection}
                    first={index === 0}
                  />
                  <PermissionedDataRow
                    section={permissionSection}
                    field={permissionField}
                    dataField={dataField}
                    label={label}
                    value={sm.opportunity.value(
                      data as Record<string, unknown>,
                      dataField,
                    )}
                    getFieldPermissionSync={getFieldPermissionSync}
                    bypassPermissions={bypassPermissions}
                    rowTestID={`summary-opportunity-row-${dataField}`}
                    isLastRow={dataField === lastOpportunityRow}
                    hidden={
                      !isSummaryRowGateSatisfied(
                        { visibleWhenFieldContains },
                        data as Record<string, unknown>,
                      )
                    }
                  />
                </React.Fragment>
              ),
            )}
          </SummarySection>
        );
      case 'siteDimensions':
        return (
          <SummarySection
            title="Site information"
            headerTestID="summary-section-header-site-information"
            filledCount={sectionMetrics.siteInformation.filled}
            totalCount={sectionMetrics.siteInformation.total}
            isExpanded={expandedSections.siteDimensions}
            onToggle={() => toggleSection('siteDimensions')}
          >
            {sectionMetrics.siteInformation.visibleRows.map(
              (
                {
                  dataField,
                  permissionSection,
                  permissionField,
                  label,
                  subsection,
                },
                index,
              ) => (
                <React.Fragment key={dataField}>
                  <SummarySubsectionHeading
                    subsection={subsection}
                    first={index === 0}
                  />
                  <PermissionedDataRow
                    section={permissionSection}
                    field={permissionField}
                    dataField={dataField}
                    label={labelFor({ label, dataField })}
                    value={valueWithSavedUnit(
                      sm.siteInformation.value(
                        data as Record<string, unknown>,
                        dataField,
                      ),
                      dataField,
                      data.dimensionUnits,
                    )}
                    getFieldPermissionSync={getFieldPermissionSync}
                    bypassPermissions={bypassPermissions}
                    rowTestID={`summary-site-information-row-${dataField}`}
                    isLastRow={dataField === lastSiteInformationRow}
                  />
                </React.Fragment>
              ),
            )}
          </SummarySection>
        );
      case 'racks':
        return (
          <SummarySection
            title="Racks"
            headerTestID="summary-section-header-racks"
            filledCount={
              useMultiRackSummary
                ? multiRackFilledCount
                : sectionMetrics.racks.filled
            }
            totalCount={
              useMultiRackSummary
                ? multiRackTotalCount
                : sectionMetrics.racks.total
            }
            isExpanded={expandedSections.racks}
            onToggle={() => toggleSection('racks')}
          >
            {useMultiRackSummary
              ? buildSummaryLocationRenderUnits({
                  tags: summaryLocationTags,
                  units: summaryRacks,
                  getLocationTagId: rack => rack.locationTagId,
                  getStatus: rack => rack.rackStatus,
                  getDisplayOrder: rack => rack.displayOrder,
                }).map(({ unit: rack }, rackIndex) => (
                  <React.Fragment
                    key={
                      rack.id != null
                        ? String(rack.id)
                        : `draft-rack-${rackIndex}`
                    }
                  >
                    <View
                      style={rackIndex > 0 ? styles.rackUnitBlock : undefined}
                    >
                      {visiblePerRackRows.map(
                        (
                          {
                            dataField,
                            permissionSection,
                            permissionField,
                            label,
                            subsection,
                          },
                          rowIndex,
                        ) => (
                          <React.Fragment key={`${rackIndex}-${dataField}`}>
                            <SummarySubsectionHeading
                              subsection={subsection}
                              first={rackIndex === 0 && rowIndex === 0}
                            />
                            <PermissionedDataRow
                              section={permissionSection}
                              field={permissionField}
                              dataField={dataField}
                              htmlSource={
                                rack as unknown as Record<string, unknown>
                              }
                              label={withSavedUnit(
                                label,
                                dataField,
                                data.dimensionUnits,
                                rack.id,
                              )}
                              value={valueWithSavedUnit(
                                withSummaryLocationFieldDisplay(
                                  dataField,
                                  rackSummaryUnitRowValue(rack, dataField),
                                  resolveSummaryLocationTagName,
                                ),
                                dataField,
                                data.dimensionUnits,
                                rack.id,
                              )}
                              getFieldPermissionSync={getFieldPermissionSync}
                              bypassPermissions={bypassPermissions}
                              rowTestID={`summary-racks-unit-${rackIndex}-row-${dataField}`}
                              isLastRow={dataField === lastPerRackSummaryField}
                            />
                          </React.Fragment>
                        ),
                      )}
                    </View>
                  </React.Fragment>
                ))
              : sectionMetrics.racks.visibleRows.map(
                  (
                    {
                      dataField,
                      permissionSection,
                      permissionField,
                      label,
                      subsection,
                    },
                    index,
                  ) => (
                    <React.Fragment key={dataField}>
                      <SummarySubsectionHeading
                        subsection={subsection}
                        first={index === 0}
                      />
                      <PermissionedDataRow
                        section={permissionSection}
                        field={permissionField}
                        dataField={dataField}
                        label={labelFor({ label, dataField })}
                        value={valueWithSavedUnit(
                          sm.racks.value(
                            data as Record<string, unknown>,
                            dataField,
                          ),
                          dataField,
                          data.dimensionUnits,
                        )}
                        getFieldPermissionSync={getFieldPermissionSync}
                        bypassPermissions={bypassPermissions}
                        rowTestID={`summary-racks-row-${dataField}`}
                        isLastRow={dataField === lastRacksRow}
                      />
                    </React.Fragment>
                  ),
                )}
          </SummarySection>
        );
      case 'facilityPower':
        return renderFacilityPowerSummaryAccordion('facilityPower');
      case 'largeUps':
        return renderFacilityPowerSummaryAccordion('largeUps');
      case 'powerDistribution':
        return renderFacilityPowerSummaryAccordion('powerDistribution');
      case 'smallUps':
        return (
          <SummarySection
            title="Small UPS"
            headerTestID="summary-section-header-small-ups"
            filledCount={
              useEditorSmallUpsSummary
                ? multiSmallUpsFilledCount
                : sectionMetrics.smallUps.filled
            }
            totalCount={
              useEditorSmallUpsSummary
                ? multiSmallUpsTotalCount
                : sectionMetrics.smallUps.total
            }
            isExpanded={expandedSections.smallUps}
            onToggle={() => toggleSection('smallUps')}
          >
            {useEditorSmallUpsSummary
              ? buildSummaryLocationRenderUnits({
                  tags: summaryLocationTags,
                  units: displaySmallUpsUnits,
                  getLocationTagId: unit => unit.locationTagId,
                  getStatus: unit => unit.status,
                  getDisplayOrder: unit => unit.displayOrder,
                }).map(({ unit }, unitIndex) => (
                  <React.Fragment
                    key={
                      unit.id != null
                        ? String(unit.id)
                        : `draft-small-ups-${unitIndex}`
                    }
                  >
                    <View
                      style={unitIndex > 0 ? styles.rackUnitBlock : undefined}
                    >
                      {visiblePerSmallUpsRows.map((row, rowIndex) => (
                        <React.Fragment key={`${unitIndex}-${row.dataField}`}>
                          <SummarySubsectionHeading
                            subsection={row.subsection}
                            first={unitIndex === 0 && rowIndex === 0}
                          />
                          <PermissionedDataRow
                            section={row.permissionSection}
                            field={row.permissionField}
                            dataField={row.dataField}
                            htmlSource={
                              unit as unknown as Record<string, unknown>
                            }
                            label={labelFor(row)}
                            value={withSummaryLocationFieldDisplay(
                              row.dataField,
                              smallUpsSummaryUnitRowValue(unit, row.dataField),
                              resolveSummaryLocationTagName,
                            )}
                            getFieldPermissionSync={getFieldPermissionSync}
                            bypassPermissions={bypassPermissions}
                            rowTestID={`summary-small-ups-unit-${unitIndex}-row-${row.dataField}`}
                            isLastRow={row.dataField === lastPerSmallUpsField}
                          />
                        </React.Fragment>
                      ))}
                    </View>
                  </React.Fragment>
                ))
              : sectionMetrics.smallUps.visibleRows.map(
                  (
                    {
                      dataField,
                      permissionSection,
                      permissionField,
                      label,
                      subsection,
                    },
                    index,
                  ) => (
                    <React.Fragment key={dataField}>
                      <SummarySubsectionHeading
                        subsection={subsection}
                        first={index === 0}
                      />
                      <PermissionedDataRow
                        section={permissionSection}
                        field={permissionField}
                        dataField={dataField}
                        label={label}
                        value={sm.smallUps.value(
                          data as Record<string, unknown>,
                          dataField,
                        )}
                        getFieldPermissionSync={getFieldPermissionSync}
                        bypassPermissions={bypassPermissions}
                        rowTestID={`summary-small-ups-row-${dataField}`}
                        isLastRow={dataField === lastSmallUpsRow}
                      />
                    </React.Fragment>
                  ),
                )}
          </SummarySection>
        );
      case 'coolingReadiness':
        return (
          <SummarySection
            title="Cooling"
            headerTestID="summary-section-header-cooling-readiness"
            filledCount={
              useEditorCoolingSystemSummary
                ? multiCoolingSystemFilledCount
                : sectionMetrics.coolingReadiness.filled
            }
            totalCount={
              useEditorCoolingSystemSummary
                ? multiCoolingSystemTotalCount
                : sectionMetrics.coolingReadiness.total
            }
            isExpanded={expandedSections.coolingReadiness}
            onToggle={() => toggleSection('coolingReadiness')}
          >
            {useEditorCoolingSystemSummary ? (
              <>
                {buildSummaryLocationRenderUnits({
                  tags: summaryLocationTags,
                  units: displayCoolingSystemUnits,
                  getLocationTagId: unit => unit.locationTagId,
                  getStatus: unit => unit.status,
                  getDisplayOrder: unit => unit.displayOrder,
                }).map(({ unit }, unitIndex) => (
                  <React.Fragment
                    key={
                      unit.id != null
                        ? String(unit.id)
                        : `draft-cooling-system-${unitIndex}`
                    }
                  >
                    <View
                      style={unitIndex > 0 ? styles.rackUnitBlock : undefined}
                    >
                      {visiblePerCoolingSystemRows.map((row, rowIndex) => (
                        <React.Fragment key={`${unitIndex}-${row.dataField}`}>
                          <SummarySubsectionHeading
                            subsection={row.subsection}
                            first={unitIndex === 0 && rowIndex === 0}
                          />
                          <PermissionedDataRow
                            section={row.permissionSection}
                            field={row.permissionField}
                            dataField={row.dataField}
                            htmlSource={
                              unit as unknown as Record<string, unknown>
                            }
                            label={withSavedUnit(
                              row.label,
                              row.dataField,
                              data.dimensionUnits,
                              unit.id,
                            )}
                            value={valueWithSavedUnit(
                              withSummaryLocationFieldDisplay(
                                row.dataField,
                                coolingSystemSummaryUnitRowValue(
                                  unit,
                                  row.dataField,
                                ),
                                resolveSummaryLocationTagName,
                              ),
                              row.dataField,
                              data.dimensionUnits,
                              unit.id,
                            )}
                            getFieldPermissionSync={getFieldPermissionSync}
                            bypassPermissions={bypassPermissions}
                            rowTestID={`summary-cooling-unit-${unitIndex}-row-${row.dataField}`}
                            isLastRow={
                              row.dataField === lastPerCoolingSystemField &&
                              (unitIndex <
                                displayCoolingSystemUnits.length - 1 ||
                                visibleCoolingSharedRows.length === 0)
                            }
                          />
                        </React.Fragment>
                      ))}
                    </View>
                  </React.Fragment>
                ))}
                {visibleCoolingSharedRows.map((row, index) => (
                  <React.Fragment key={row.dataField}>
                    <SummarySubsectionHeading
                      subsection={row.subsection}
                      first={
                        displayCoolingSystemUnits.length === 0 && index === 0
                      }
                    />
                    <PermissionedDataRow
                      section={row.permissionSection}
                      field={row.permissionField}
                      dataField={row.dataField}
                      label={labelFor(row)}
                      value={sm.coolingReadiness.value(
                        data as Record<string, unknown>,
                        row.dataField,
                      )}
                      getFieldPermissionSync={getFieldPermissionSync}
                      bypassPermissions={bypassPermissions}
                      rowTestID={`summary-cooling-row-${row.dataField}`}
                      isLastRow={row.dataField === lastCoolingSharedField}
                    />
                  </React.Fragment>
                ))}
              </>
            ) : (
              sectionMetrics.coolingReadiness.visibleRows.map(
                (
                  {
                    dataField,
                    permissionSection,
                    permissionField,
                    label,
                    subsection,
                  },
                  index,
                ) => (
                  <React.Fragment key={dataField}>
                    <SummarySubsectionHeading
                      subsection={subsection}
                      first={index === 0}
                    />
                    <PermissionedDataRow
                      section={permissionSection}
                      field={permissionField}
                      dataField={dataField}
                      label={labelFor({ label, dataField })}
                      value={valueWithSavedUnit(
                        sm.coolingReadiness.value(
                          data as Record<string, unknown>,
                          dataField,
                        ),
                        dataField,
                        data.dimensionUnits,
                      )}
                      getFieldPermissionSync={getFieldPermissionSync}
                      bypassPermissions={bypassPermissions}
                      rowTestID={`summary-cooling-row-${dataField}`}
                      isLastRow={dataField === lastCoolingReadinessRow}
                    />
                  </React.Fragment>
                ),
              )
            )}
          </SummarySection>
        );
      case 'liquidCooling':
        return (
          <SummarySection
            title="Liquid Cooling"
            headerTestID="summary-section-header-liquid-cooling"
            filledCount={
              useEditorLiquidCoolingCduSummary
                ? multiLiquidCoolingCduFilledCount
                : sectionMetrics.liquidCooling.filled
            }
            totalCount={
              useEditorLiquidCoolingCduSummary
                ? multiLiquidCoolingCduTotalCount
                : sectionMetrics.liquidCooling.total
            }
            isExpanded={expandedSections.liquidCooling}
            onToggle={() => toggleSection('liquidCooling')}
          >
            {useEditorLiquidCoolingCduSummary ? (
              <>
                {visibleLiquidCoolingSharedRows.map((row, index) => (
                  <React.Fragment key={row.dataField}>
                    <SummarySubsectionHeading
                      subsection={row.subsection}
                      first={index === 0}
                    />
                    <PermissionedDataRow
                      section={row.permissionSection}
                      field={row.permissionField}
                      dataField={row.dataField}
                      label={labelFor(row)}
                      value={sm.liquidCooling.value(
                        data as Record<string, unknown>,
                        row.dataField,
                      )}
                      getFieldPermissionSync={getFieldPermissionSync}
                      bypassPermissions={bypassPermissions}
                      rowTestID={`summary-liquid-cooling-row-${row.dataField}`}
                      isLastRow={
                        row.dataField === lastLiquidCoolingSharedField &&
                        displayLiquidCoolingCduUnits.length === 0
                      }
                    />
                  </React.Fragment>
                ))}
                {buildSummaryLocationRenderUnits({
                  tags: summaryLocationTags,
                  units: displayLiquidCoolingCduUnits,
                  getLocationTagId: unit => unit.locationTagId,
                  getStatus: unit => unit.status,
                  getDisplayOrder: unit => unit.displayOrder,
                }).map(({ unit }, unitIndex) => (
                  <React.Fragment
                    key={
                      unit.id != null
                        ? String(unit.id)
                        : `draft-liquid-cooling-cdu-${unitIndex}`
                    }
                  >
                    <View
                      style={unitIndex > 0 ? styles.rackUnitBlock : undefined}
                    >
                      {visiblePerLiquidCoolingCduRows.map((row, rowIndex) => (
                        <React.Fragment key={`${unitIndex}-${row.dataField}`}>
                          <SummarySubsectionHeading
                            subsection={row.subsection}
                            first={
                              unitIndex === 0 &&
                              rowIndex === 0 &&
                              visibleLiquidCoolingSharedRows.length === 0
                            }
                          />
                          <PermissionedDataRow
                            section={row.permissionSection}
                            field={row.permissionField}
                            dataField={row.dataField}
                            htmlSource={
                              unit as unknown as Record<string, unknown>
                            }
                            label={labelFor(row)}
                            value={withSummaryLocationFieldDisplay(
                              row.dataField,
                              liquidCoolingCduSummaryUnitRowValue(
                                unit,
                                row.dataField,
                              ),
                              resolveSummaryLocationTagName,
                            )}
                            getFieldPermissionSync={getFieldPermissionSync}
                            bypassPermissions={bypassPermissions}
                            rowTestID={`summary-liquid-cooling-unit-${unitIndex}-row-${row.dataField}`}
                            isLastRow={
                              row.dataField === lastPerLiquidCoolingCduField
                            }
                          />
                        </React.Fragment>
                      ))}
                    </View>
                  </React.Fragment>
                ))}
              </>
            ) : (
              sectionMetrics.liquidCooling.visibleRows.map(
                (
                  {
                    dataField,
                    permissionSection,
                    permissionField,
                    label,
                    subsection,
                  },
                  index,
                ) => (
                  <React.Fragment key={dataField}>
                    <SummarySubsectionHeading
                      subsection={subsection}
                      first={index === 0}
                    />
                    <PermissionedDataRow
                      section={permissionSection}
                      field={permissionField}
                      dataField={dataField}
                      label={labelFor({ label, dataField })}
                      value={valueWithSavedUnit(
                        sm.liquidCooling.value(
                          data as Record<string, unknown>,
                          dataField,
                        ),
                        dataField,
                        data.dimensionUnits,
                      )}
                      getFieldPermissionSync={getFieldPermissionSync}
                      bypassPermissions={bypassPermissions}
                      rowTestID={`summary-liquid-cooling-row-${dataField}`}
                      isLastRow={dataField === lastLiquidCoolingRow}
                    />
                  </React.Fragment>
                ),
              )
            )}
          </SummarySection>
        );
      case 'supportingDocuments':
        return (
          <SummarySection
            title="Supporting Documents"
            headerTestID="summary-section-header-supporting-documents"
            filledCount={totalAttachmentCount}
            totalCount={totalAttachmentCount}
            countOnly
            isExpanded={expandedSections.supportingDocuments}
            onToggle={() => toggleSection('supportingDocuments')}
          >
            {totalAttachmentCount === 0 ? (
              <DataRow
                label="Documents"
                value=""
                testID="summary-supporting-documents-empty"
                isLastRow
              />
            ) : (
              <View>
                {attachmentsByStage.map((group, groupIndex) => (
                  <View key={group.stage}>
                    <Text style={styles.subsectionTitle}>
                      {stripStageNumber(group.label ?? '')}
                    </Text>
                    {group.items.map(
                      ({ sectionId, sectionLabel, attachment }, index) => {
                        const isLastAttachment =
                          groupIndex === attachmentsByStage.length - 1 &&
                          index === group.items.length - 1;
                        return (
                          <SummaryAttachmentRow
                            key={
                              attachment.id != null
                                ? attachment.id
                                : `${sectionId}-${index}`
                            }
                            attachment={attachment}
                            caption={
                              attachment.captions ||
                              sectionLabel ||
                              supportingDocumentsAttachmentCaption(
                                sectionId,
                                group.label ?? '',
                                group.stage,
                              )
                            }
                            testID={`summary-supporting-documents-row-${String(
                              attachment.id ?? `${sectionId}-${index}`,
                            )}`}
                            isLastRow={isLastAttachment}
                            onOpen={summaryMedia.openCarousel}
                          />
                        );
                      },
                    )}
                  </View>
                ))}
              </View>
            )}
          </SummarySection>
        );
      default:
        return null;
    }
  };

  return (
    <SummaryMediaContext.Provider value={summaryMedia}>
      <View style={styles.container}>
        {isSmallScreen ? (
          <View style={styles.header}>
            <View style={styles.headerText}>
              <Text style={[styles.pageTitle, styles.pageTitleCompact]}>
                Summary
              </Text>
            </View>
            <View style={[styles.headerButtons, styles.headerButtonsMobile]}>
              <TouchableOpacity
                style={[
                  styles.expandCollapseButton,
                  styles.expandCollapseButtonCompact,
                ]}
                onPress={
                  allSectionsExpanded ? collapseAllSections : expandAllSections
                }
                accessibilityRole="button"
                accessibilityLabel={
                  allSectionsExpanded
                    ? 'Collapse all summary sections'
                    : 'Expand all summary sections'
                }
                testID="summary-header-expand-collapse"
              >
                {allSectionsExpanded ? (
                  <Minimize size={14} color="#6c757d" />
                ) : (
                  <Expand size={14} color="#6c757d" />
                )}
              </TouchableOpacity>
            </View>
          </View>
        ) : (
          <View style={styles.headerDesktop}>
            <View style={styles.headerTopRowWide}>
              <Text
                style={[styles.pageTitle, styles.pageTitleInTopRow]}
                accessibilityRole="header"
              >
                Summary
              </Text>
              <View style={styles.headerButtons}>
                <TouchableOpacity
                  style={styles.expandCollapseButton}
                  onPress={
                    allSectionsExpanded
                      ? collapseAllSections
                      : expandAllSections
                  }
                  accessibilityRole="button"
                  accessibilityLabel={
                    allSectionsExpanded
                      ? 'Collapse all summary sections'
                      : 'Expand all summary sections'
                  }
                  testID="summary-header-expand-collapse"
                >
                  {allSectionsExpanded ? (
                    <Minimize
                      size={16}
                      color="#6c757d"
                      style={styles.buttonIcon}
                    />
                  ) : (
                    <Expand
                      size={16}
                      color="#6c757d"
                      style={styles.buttonIcon}
                    />
                  )}
                  <Text style={styles.expandCollapseButtonText}>
                    {allSectionsExpanded ? 'Collapse All' : 'Expand All'}
                  </Text>
                </TouchableOpacity>
              </View>
            </View>
            <Text style={[styles.pageSubtitle, styles.pageSubtitleUnderTitle]}>
              Review your selections from all steps
            </Text>
          </View>
        )}

        {orderedSummarySectionKeys.map(sectionKey => (
          <React.Fragment key={sectionKey}>
            {renderSummarySectionByKey(sectionKey)}
          </React.Fragment>
        ))}

        {/* Impersonation Activity (admin-only) */}
        {isAdmin && requestId !== undefined && (
          <ImpersonationActivitySection
            requestId={requestId}
            isAdmin={isAdmin}
          />
        )}
        <AttachmentCarouselModal {...carouselProps} />
      </View>
    </SummaryMediaContext.Provider>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  header: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: 24,
  },
  /** Desktop / tablet: title + actions on first row; subtitle on its own row below (not inline with PDF). */
  headerDesktop: {
    width: '100%',
    marginBottom: 24,
  },
  headerTopRowWide: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    width: '100%',
  },
  headerText: {
    flex: 1,
    marginRight: 16,
  },
  headerButtons: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
  },
  /** Narrow viewports: tighter gap next to PDF + icon-only expand. */
  headerButtonsMobile: {
    gap: 8,
  },
  expandCollapseButton: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 8,
    paddingVertical: 8,
    ...Platform.select({
      web: {
        cursor: 'pointer',
      },
    }),
  },
  expandCollapseButtonCompact: {
    paddingHorizontal: 6,
    paddingVertical: 6,
    justifyContent: 'center',
  },
  expandCollapseButtonText: {
    color: '#6c757d',
    fontSize: 14,
    fontWeight: '500',
    marginLeft: 6,
  },
  buttonIcon: {
    marginRight: 2,
  },
  pageTitle: {
    fontSize: 24,
    fontWeight: '700',
    color: '#ff6b35',
    marginBottom: 8,
  },
  /** Summary title when it shares a row with PDF/expand (desktop top row). */
  pageTitleInTopRow: {
    marginBottom: 0,
  },
  /** Tighter chrome when subtitle is omitted (narrow / mobile). */
  pageTitleCompact: {
    marginBottom: 4,
  },
  pageSubtitle: {
    fontSize: 16,
    color: '#666',
  },
  pageSubtitleUnderTitle: {
    marginTop: 8,
  },
  summarySection: {
    marginBottom: 16,
    backgroundColor: '#fff',
    borderRadius: 8,
    borderWidth: 1,
    borderColor: '#e0e0e0',
    overflow: 'hidden',
  },
  sectionHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: 16,
    backgroundColor: '#f8f9fa',
    borderBottomWidth: 1,
    borderBottomColor: '#e0e0e0',
  },
  sectionTitle: {
    fontSize: 16,
    fontWeight: '600',
    color: '#333',
  },
  sectionRight: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  countBadge: {
    backgroundColor: '#ff6b35',
    paddingHorizontal: 8,
    paddingVertical: 4,
    borderRadius: 12,
    marginRight: 8,
  },
  countText: {
    fontSize: 12,
    color: '#fff',
    fontWeight: '600',
  },
  chevron: {
    marginLeft: 4,
  },
  sectionContent: {
    padding: 16,
  },
  subsectionTitle: {
    fontSize: 14,
    fontWeight: '600',
    color: '#333',
    marginTop: 12,
    marginBottom: 6,
  },
  subsectionTitleFirst: {
    marginTop: 0,
  },
  rackUnitBlock: {
    marginTop: 16,
    paddingTop: 12,
    borderTopWidth: 1,
    borderTopColor: '#e9ecef',
  },
  rackUnitTitle: {
    fontSize: 15,
    fontWeight: '700',
    color: '#e55a2b',
    marginBottom: 8,
  },
  dataRow: {
    flexDirection: 'row',
    paddingVertical: 8,
    borderBottomWidth: 1,
    borderBottomColor: '#f0f0f0',
    alignItems: 'flex-start',
  },
  dataRowLast: {
    borderBottomWidth: 0,
  },
  /** Split space with the value column so long labels are not squeezed into 140px with a huge empty gap on wide layouts. */
  dataLabelContainer: {
    flex: 1,
    flexBasis: 0,
    minWidth: 0,
    marginRight: 12,
    paddingRight: 4,
  },
  dataLabel: {
    fontSize: 14,
    color: '#666',
    fontWeight: '500',
  },
  dataValueContainer: {
    flex: 1,
    flexBasis: 0,
    minWidth: 0,
  },
  dataValue: {
    fontSize: 14,
    color: '#333',
    lineHeight: 20,
  },
  dataValueNotSpecified: {
    fontSize: 14,
    color: '#999',
    lineHeight: 20,
    fontStyle: 'italic',
  },
  stageGateNotice: {
    fontSize: 14,
    lineHeight: 20,
    color: '#666',
  },
});
